/**
 * An emoji, with real artwork for flags.
 *
 * Windows ships no flag glyphs. The characters are valid and stored correctly —
 * the same album shows a Spanish flag on a phone — but Segoe UI Emoji has no
 * picture for a regional-indicator pair, so 🇪🇸 renders as the letters "ES".
 * Explaining that in a caption, which is what we did, does not make a group
 * called "ES Spain" look any less broken to the person who chose a flag.
 *
 * So flags, and only flags, are drawn from bundled SVGs. Every other emoji is
 * left as text: the system font draws those correctly everywhere, and swapping
 * three thousand glyphs for images to fix two hundred and fifty would cost a
 * megabyte and make every emoji look subtly foreign to its own platform.
 *
 * The SVGs are emitted as separate files and referenced by URL, so a page
 * showing four flags fetches four small files rather than a sprite sheet of
 * two hundred and seventy — 2.4 MB that would otherwise sit in front of the
 * first paint.
 */

import { useMemo } from "react";

/*
 * Vite resolves this at build time into a map of module path -> emitted URL.
 * `eager` is right here despite the size: the values are short URL strings, not
 * the SVGs themselves, and a lazy glob would make every flag a promise and
 * every flag render a flash of the letter fallback.
 */
const FLAG_URLS = import.meta.glob<string>(
  "../../../../../node_modules/flag-icons/flags/4x3/*.svg",
  { eager: true, query: "?url", import: "default" },
);

/** Lower-case ISO code -> URL, keyed off the filename. */
const FLAGS = new Map<string, string>(
  Object.entries(FLAG_URLS).flatMap(([path, url]) => {
    const code = path.slice(path.lastIndexOf("/") + 1, -".svg".length);
    return url ? [[code, url] as [string, string]] : [];
  }),
);

/**
 * The ISO 3166-1 alpha-2 code inside a flag emoji, or null if it is not one.
 *
 * A flag is exactly two regional-indicator symbols (U+1F1E6…U+1F1FF), each
 * standing for one letter. Anything else — 🏖️, a subdivision flag built from a
 * tag sequence, a flag followed by a variation selector — returns null and is
 * drawn as text, which is what it wants anyway.
 */
export function flagCode(emoji: string | null | undefined): string | null {
  if (!emoji) return null;

  const points = [...emoji];
  if (points.length !== 2) return null;

  let code = "";
  for (const point of points) {
    const value = point.codePointAt(0);
    if (value === undefined || value < 0x1f1e6 || value > 0x1f1ff) return null;
    code += String.fromCharCode(value - 0x1f1e6 + 97);
  }

  return code;
}

interface EmojiIconProps {
  emoji: string | null | undefined;
  /** Read out where the emoji carries meaning rather than decorating a label. */
  label?: string;
  className?: string;
}

export function EmojiIcon({ emoji, label, className }: EmojiIconProps) {
  const url = useMemo(() => {
    const code = flagCode(emoji);
    return code ? (FLAGS.get(code) ?? null) : null;
  }, [emoji]);

  if (!emoji) return null;

  if (url) {
    return (
      <img
        className={className ? `fc-flag ${className}` : "fc-flag"}
        src={url}
        alt={label ?? ""}
        /*
         * Not lazy: these are inside icons that are already on screen when they
         * mount, and a lazy flag pops in a beat after the name it belongs to.
         */
        decoding="async"
        draggable={false}
      />
    );
  }

  return (
    <span className={className} role={label ? "img" : undefined} aria-label={label}>
      {emoji}
    </span>
  );
}
